import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../LanguageContext";

function RiskBadge({ level, result }) {
  const isAnomaly = result?.includes("Anomaly") || result?.includes("Potential");
  const text = level || (isAnomaly ? "moderate" : "low");
  const cfg = {
    low:      "bg-emerald-50 text-emerald-700 border-emerald-200",
    moderate: "bg-amber-50 text-amber-700 border-amber-200",
    high:     "bg-rose-50 text-rose-700 border-rose-200",
  };
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${cfg[text?.toLowerCase()] || cfg.low} capitalize`}>
      {text}
    </span>
  );
}

function TypeIcon({ type }) {
  if (type === "voice") {
    return (
      <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
        <svg className="w-5 h-5 text-amber-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
        </svg>
      </div>
    );
  }
  return (
    <div className="w-9 h-9 rounded-xl bg-stone-100 flex items-center justify-center flex-shrink-0">
      <svg className="w-5 h-5 text-stone-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    </div>
  );
}

function HistoryCard({ entry, onDelete }) {
  const { t } = useLanguage();
  const d = t.dashboard;
  const [expanded, setExpanded] = useState(false);
  const date = new Date(entry.date);
  const timeAgo = (() => {
    const diff = Date.now() - date.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return d.justNow;
    if (mins < 60) return `${mins}${d.minAgo}`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}${d.hrAgo}`;
    return date.toLocaleDateString();
  })();

  return (
    <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
      <div className="p-4 flex items-start gap-3">
        <TypeIcon type={entry.type} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-medium text-stone-900 truncate">{entry.filename || d.unknown}</p>
              <p className="text-xs text-stone-400 mt-0.5">
                {timeAgo} · {entry.type === "voice" ? d.voice : d.skin} {d.analysis}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <RiskBadge level={entry.risk_level} result={entry.result} />
              <button onClick={() => onDelete(entry.id)}
                className="text-stone-300 hover:text-rose-400 transition-colors" title="Delete">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          {entry.result && <p className="text-xs text-stone-600 mt-1.5 font-medium">{entry.result}</p>}
          {(entry.observations || entry.explanation) && (
            <button onClick={() => setExpanded((e) => !e)}
              className="text-xs text-amber-700 mt-1.5 hover:underline">
              {expanded ? d.showLess : d.showDetails}
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t border-stone-50 pt-3 animate-fade-in">
          {(entry.observations || entry.explanation) && (
            <p className="text-xs text-stone-600 leading-relaxed mb-3">
              {entry.observations || entry.explanation}
            </p>
          )}
          {entry.recommendations && entry.recommendations.length > 0 && (
            <ul className="space-y-1">
              {entry.recommendations.map((r, i) => (
                <li key={i} className="text-xs text-stone-500 flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">•</span>
                  {r}
                </li>
              ))}
            </ul>
          )}
          {entry.confidence !== undefined && (
            <p className="text-xs text-stone-400 mt-2">{d.confidence}: {Math.round(entry.confidence * 100)}%</p>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-5">
      <p className="text-2xl font-bold text-stone-900">{value}</p>
      <p className="text-sm font-medium text-stone-600 mt-0.5">{label}</p>
      {sub && <p className="text-xs text-stone-400 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function Dashboard() {
  const { t } = useLanguage();
  const d = t.dashboard;
  const [history, setHistory] = useState([]);

  useEffect(() => {
    setHistory(JSON.parse(localStorage.getItem("wl_history") || "[]"));
  }, []);

  const deleteEntry = (id) => {
    const updated = history.filter((e) => e.id !== id);
    setHistory(updated);
    localStorage.setItem("wl_history", JSON.stringify(updated));
  };

  const clearAll = () => {
    if (window.confirm(d.confirmClear)) {
      setHistory([]);
      localStorage.removeItem("wl_history");
    }
  };

  const voiceCount   = history.filter((e) => e.type === "voice").length;
  const skinCount    = history.filter((e) => e.type === "skin").length;
  const anomalyCount = history.filter(
    (e) => e.result?.includes("Anomaly") || e.risk_level === "high" || e.risk_level === "moderate"
  ).length;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 animate-fade-in">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">{d.title}</h1>
          <p className="text-sm text-stone-500 mt-1">{d.subtitle}</p>
        </div>
        {history.length > 0 && (
          <button onClick={clearAll} className="text-xs text-rose-500 hover:text-rose-700 font-medium transition-colors">
            {d.clearAll}
          </button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4 mb-8">
        <StatCard label={d.totalScans} value={history.length} sub={d.allTime} />
        <StatCard label={d.voiceScans} value={voiceCount} />
        <StatCard label={d.skinScans}  value={skinCount} />
      </div>

      {anomalyCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 flex items-start gap-3">
          <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <p className="text-sm font-semibold text-amber-800">
              {anomalyCount} {anomalyCount > 1 ? d.flaggedScans : d.flaggedScan}
            </p>
            <p className="text-xs text-amber-700 mt-0.5">{d.flaggedNote}</p>
          </div>
        </div>
      )}

      {history.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-16 h-16 rounded-2xl bg-stone-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-stone-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <p className="text-stone-500 font-medium mb-1">{d.noAnalyses}</p>
          <p className="text-sm text-stone-400 mb-6">{d.noAnalysesSub}</p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/voice"
              className="px-4 py-2 bg-amber-700 hover:bg-amber-800 text-white text-sm font-semibold rounded-xl transition-colors">
              {d.voiceAnalysis}
            </Link>
            <Link to="/skin"
              className="px-4 py-2 bg-white border border-stone-200 hover:border-amber-300 text-stone-700 text-sm font-semibold rounded-xl transition-colors">
              {d.skinAnalysis}
            </Link>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {history.map((entry) => (
            <HistoryCard key={entry.id} entry={entry} onDelete={deleteEntry} />
          ))}
        </div>
      )}

      {history.length > 0 && (
        <p className="text-xs text-stone-400 text-center mt-6">{d.localNote}</p>
      )}
    </div>
  );
}
